import {Stroke} from '../node_modules/ol/style';

import drawInteraction from '../node_modules/ol/interaction/Draw.js';
import selectInteraction from '../node_modules/ol/interaction/Select.js'; // used to check if instance of selectInteraction
import Feature from 'ol/Feature.js';
import Polygon from 'ol/geom/Polygon.js';

import StyleControls from './StyleControls.js';
import {hexToRgb, parseCSS} from './util.js';
import postFeatureToServer from './postFeatureToServer.js';  // wfs.js is required regardless of what vscode indicates!

/**
 * FeatureStyleControls Constructor
 *
 * called in ModifyVectorFeatures.js
 *
 * Instantiation creates UI style controls and assigns 'on change' functions to them.
 * The 'on change' functions assign the user selected styles to all selected features,
 * which assumes that all selected features already have a style!
 *
 * The public method setControls(feature) sets
 * the UI control values to the feature's values.
 */

export default function() {
  let stylecontrols;  // instance of rwg.StyleControls
  const deleteFeatureButton = document.querySelector(['button#deleteFeature']);

  /** private function
   *  accepts string formatted as either: 'rgba(rr, gg, bb, a.a)' or '#rrggbb'
   *  returns object:  { color: '#rrggbb', opacity: a.a}
   */
  function parseColorAndOpacity(val) {
    var result = {};
    if (val.indexOf('rgba') > -1) {
      var rgba = val.replace(/[rgba( )]/g, '').split(',');
      result.color = '#' +
        ('0' + parseInt(rgba[0],10).toString(16)).slice(-2) +
        ('0' + parseInt(rgba[1],10).toString(16)).slice(-2) +
        ('0' + parseInt(rgba[2],10).toString(16)).slice(-2);
      result.opacity = parseFloat(rgba[3]);
    } else if (val.indexOf('#' > -1)) {
      result.color = val;
      result.opacity = 1;
    }
    return result;
  }

  function deleteFeature() {
    // want one, and only one, feature to be selected
    if (rwg.selection.selectInteraction.getFeatures().getLength() === 1) {
      if (confirm('Deleted selected feature?')) {
        let selectedFeature = rwg.selection.selectInteraction.getFeatures().getArray()[0];

        let tooltip = selectedFeature.get('tooltipOverlay_');
        if (tooltip) tooltip.remove();
        if (rwg.config.saveFeatures) postFeatureToServer('delete', selectedFeature);
        rwg.selection.selectInteraction.getFeatures().clear();
        let vectorLayer = rwg.selection.selectInteraction.getLayer(selectedFeature)
        vectorLayer.getSource().removeFeature(selectedFeature);
        document.querySelector('#featureDiv').style.display = 'none';
        if (document.querySelector('#drawHelp')) {
          document.querySelector('#drawHelp').style.display = 'block'
        }
        rwg.tabs.activate('side-legend')
      }
    }
  }

  deleteFeatureButton.addEventListener('click', deleteFeature);
  document.addEventListener("keydown", evt => {
    // want to be sure that a keyboard delete isn't coming from an <input> or anything else
    if (evt.key === "Delete") {
      if (evt.target.getAttribute('data-ol-map') ||   // if the focus is the map
          evt.target === document.body ||             // or the body
          (evt.target.type === 'text' && evt.target.value === '') // or the focus is an empty text input (probably "label" becasue we set its focus on feature selection)
      ) {
        deleteFeature();
      }
    }
  });

  function setFont() {
    let value = this.value;
    let fontCharacteristic = this.id.replace('feature-', ''); // fontweight | fontsize | fontfamily | fontoutlinewidth
    let style, font, parsedFont, outlinewidth;
    rwg.selection.selectInteraction.getFeatures().getArray().forEach( feature => {
      style = feature.getStyle();
      if (fontCharacteristic === 'fontoutlinewidth') {
        outlinewidth = parseInt(value);
        if (outlinewidth > 0) {
          if (style[0].getText().getStroke()) {
            style[0].getText().getStroke().setWidth(outlinewidth);
          } else {
            style[0].getText().setStroke( new Stroke({color: document.getElementById('feature-fontoutlinecolor').value, width: outlinewidth}) );
          }
        } else {
          style[0].getText().setStroke(null);
        }
      } else {
        parsedFont = parseCSS('font: ' + style[0].getText().getFont());
        // parsedFont = parseCSS(featureStyleText.getFont());

        font = fontCharacteristic === 'fontweight' ? value : parsedFont['fontWeight'];
        font += ' ';
        font += fontCharacteristic === 'fontsize' ? value : parsedFont['fontSize'];
        font += ' ';
        font += fontCharacteristic === 'fontfamily' ? value : parsedFont['fontFamily'];

        style[0].getText().setFont(font);
        // featureStyleText.setFont(font);
      }
      feature.setStyle(style);
      feature.set('hasOwnStyle_', true);
      feature.changed();  // increment to ol revision counter
    });
  }

  // called by stylecontrols when uses picks
  function setLineWidth() { // this does lines and polygon outlines
    let style;
    rwg.selection.selectInteraction.getFeatures().getArray().forEach( feature => {
      style = feature.getStyle();
      style[0].getStroke().setWidth(parseInt(this.value));    // non-jquery
      feature.setStyle(style);
      feature.set('rwg_selectedFeaturehasOwnStyle', true);
      feature.changed();  // increment to ol revision counter
    });
  }

  // called by stylecontrols when uses picks
  function setLineOpacity() { // this does lines and polygon outlines
    let style, rgba, stroke, strokeColorAndOpacity;
    rwg.selection.selectInteraction.getFeatures().getArray().forEach( feature => {
      style = feature.getStyle();
      stroke = style[0].getStroke();
      strokeColorAndOpacity = parseColorAndOpacity(stroke.getColor());
      rgba = `rgba(${hexToRgb(strokeColorAndOpacity.color)},${this.value})`;
      stroke = style[0].getStroke();
      if (stroke === null) {
        style[0].setStroke( new Stroke({
          color: rgba
        }));
      } else {
        style[0].getStroke().setColor(rgba);
      }
      feature.setStyle(style);
      feature.set('hasOwnStyle_', true);
      feature.changed();  // increment to ol revision counter
    });
  }

  function setFillColor(color) {
    rwg.selection.selectInteraction.getFeatures().getArray().forEach( feature => {
      let fill = feature.getStyle()[0].getFill()
      let fillColorAndOpacity = parseColorAndOpacity(fill.getColor())
      let opacity = fillColorAndOpacity.opacity
      // it's confusing to the user if they set a fill color but don't see it take effect because opacity === 0
      if (opacity === 0) {
        opacity = 0.5
        stylecontrols.setFillOpacity(opacity)
      }
      let rgba = 'rgba(' +  hexToRgb(color) + ',' + opacity + ')'
      fill.setColor(rgba)
      feature.set('hasOwnStyle_', true)
      feature.changed()  // increment to ol revision counter
    })
  }

  function setLineColor(color) {
    let stroke;
    let strokeColorAndOpacity;
    let rgba;
    rwg.selection.selectInteraction.getFeatures().getArray().forEach( feature => {
      stroke = feature.getStyle()[0].getStroke();
      strokeColorAndOpacity = parseColorAndOpacity(stroke.getColor());
      rgba = 'rgba(' +  hexToRgb(color) + ',' + strokeColorAndOpacity.opacity + ')';
      stroke.setColor(rgba);
      feature.set('hasOwnStyle_', true);
      feature.changed();  // increment to ol revision counter
    });
  }

  function setFontColor(color) {
    rwg.selection.selectInteraction.getFeatures().getArray().forEach( feature => {
      feature.getStyle()[0].getText().getFill().setColor(color);
      feature.set('hasOwnStyle_', true);
      feature.changed();  // increment to ol revision counter
    });
  }

  function setFontOutlineColor(color) {
    let style;
    rwg.selection.selectInteraction.getFeatures().getArray().forEach( feature => {
      style = feature.getStyle();
      if (style[0].getText().getStroke()) {
        style[0].getText().getStroke().setColor(color);
      } else {
        let width = parseInt(document.getElementById('feature-fontoutlinewidth').value) || 3;
        style[0].getText().setStroke(new Stroke({color: color, width: width}));
        document.getElementById('feature-fontoutlinewidth').value = width;
      }
      feature.set('hasOwnStyle_', true);
      feature.changed();  // increment to ol revision counter
    });
  }

  function setColor() {  // figure out which color picker we're answering (there are 5 different ones)
    switch(this.id) {
      case 'feature-linecolor':
        setLineColor(this.value);
        break;
      case 'feature-outlinecolor':
        setLineColor(this.value);
        break;
      case 'feature-fillcolor':
        setFillColor(this.value);
        break;
      case 'feature-fontcolor':
        setFontColor(this.value);
        break;
      case 'feature-fontoutlinecolor':
        setFontOutlineColor(this.value);
        break;
    }
  }

  function setFillOpacity() {
    let fill, fillColorAndOpacity;
    rwg.selection.selectInteraction.getFeatures().getArray().forEach( feature => {
      fill = feature.getStyle()[0].getFill();
      fillColorAndOpacity = parseColorAndOpacity(fill.getColor());
      fill.setColor(`rgba(${hexToRgb(fillColorAndOpacity.color)},${this.value})`);
      feature.set('hasOwnStyle_', true);
      feature.changed();  // increment to ol revision counter
    });
  }

  function addRing() {
    // https://spatial-dev.guru/2022/01/22/draw-holes-in-openlayers-polygon/
    let feature = rwg.selection.selectInteraction.getFeatures().getArray()[0]
    // let source = rwg.sources.get('lyrDraw');
    let ringInteraction = new drawInteraction({type: 'Polygon'})

    rwg.map.getInteractions().forEach((interaction) => {
      if (interaction instanceof selectInteraction) {
        interaction.setActive(false);
      }
    });

    rwg.map.addInteraction(ringInteraction)
    // ringInteraction.on('drawstart', (evt) => {
    //   // evt.stopPropagation()
    //   // evt.stopImmediatePropagation()
    //   evt.preventDefault()
    // })
    ringInteraction.on('drawend', (evt) => {
      feature.getGeometry().appendLinearRing(evt.feature.getGeometry())
      rwg.map.removeInteraction(ringInteraction)
      rwg.map.getInteractions().forEach((interaction) => {
        if (interaction instanceof selectInteraction) {
          interaction.setActive(true);
        }
      });

    })
  }

  /* Create a new instance of StyleControls, passing in all the event handlers which respond
     to the style control "change" events and apply the user's selection to the selected feature(s).
     There are 7 different style types: font, color, width, opacity, etc.
  */
  stylecontrols = new StyleControls({
    prefix: 'feature-',
    font: setFont,
    colorpicker: setColor,
    linewidth: setLineWidth,
    outlinewidth: setLineWidth,
    lineopacity: setLineOpacity,
    outlineopacity: setLineOpacity,
    fillopacity: setFillOpacity,
    addring: addRing,
  });
  document.querySelector('#stylecontrols').append(stylecontrols.controls)

  /**
   * public function that sets the style control values based on the feature's styles
   * called when a feature gets selected or created
   * called from ModifyVectorFeature.js and Draw.js
   */
  // this.setControls = function(feature, selectInteraction) {
  function setControls(feature, selectInteraction) {
    let geomType = feature.getGeometry().getType();
    let fillColorAndOpacity;
    let strokeColorAndOpacity;
    let fontOutline;
    let featureVals = {};
    let style;
    let featureStyle = feature.getStyle();
    // if feature has a style grab it or else get layer's style. hasOwnStyle_ could be used here instead
    if (featureStyle && feature.getStyle()[0]) {
      style = feature.getStyle()[0];
    } else {  // need to use ./feature-getLayer.js and/or prototype https://stackoverflow.com/questions/31297721/how-to-get-a-layer-from-a-feature-in-openlayers-3
      style = rwg.map.getLayerByTitle('lyrDraw').getStyle()(feature, rwg.map.getView().getResolution())[0];
    }
    let fill = style.getFill();
    let stroke = style.getStroke();
    let text = style.getText();

    // switch to whichever tab contains 'featureDiv'. For cpc it will be 'infoTab', for ass map it will be 'drawTab'
    // activateTab(rwg.sidebarTabs, {'name': document.getElementById('featureDiv').parentElement.id});
    let targetTab = document.getElementById('featureDiv').parentElement.getAttribute('rwg-tab')
    rwg.tabs.activate(targetTab)
    document.querySelector('#featureDiv').style.display = '';
    if (document.querySelector('#drawHelp')) {
      document.querySelector('#drawHelp').style.display = 'none'
    }

    // only have a 'delete' button when only one feature is selected
    if (selectInteraction.getFeatures().getArray().length === 1 &&
        selectInteraction.getLayer(selectInteraction.getFeatures().getArray()[0]))  // this prevents delete button on newly drawn feature
    {
      deleteFeatureButton.style.display = 'block';
      deleteFeatureButton.selectInteraction = selectInteraction;
    } else {
      deleteFeatureButton.style.display = 'none';
    }

    if (geomType.indexOf('Polygon') > -1 ) {
      if (fill) {
        fillColorAndOpacity = parseColorAndOpacity( fill.getColor() );
        featureVals.fillcolor = fillColorAndOpacity.color;
        featureVals.fillopacity = fillColorAndOpacity.opacity;
      } else {
        console.log('no fill style[0]');  // eslint-disable-line no-console
        alert('need code here to add a default fill to polygon'); // maybe white w/ opacity = 0
      }
      document.querySelector('#style-poly').style.display = '';
    } else {
      document.querySelector('#style-poly').style.display = 'none';
    }

    if (stroke) {
      strokeColorAndOpacity = parseColorAndOpacity(stroke.getColor());
      featureVals.outlinecolor   = featureVals.linecolor   = strokeColorAndOpacity.color;
      featureVals.outlineopacity = featureVals.lineopacity = strokeColorAndOpacity.opacity;
      featureVals.outlinewidth  = featureVals.linewidth   = stroke.getWidth();
    } else {
      console.log('no stroke style[0]');  // eslint-disable-line no-console
    }

    geomType.indexOf('Line') > -1  ?
      document.querySelector('#style-line').style.display = '' :
      document.querySelector('#style-line').style.display = 'none'

    // labels & attributes are a little different, rather than assigning handlers and values
    // to existing HTML elements we're creating the element as well as populating it.
    // attributes(feature);  // moved to ModifyVectorFeature.js 2017-11-28

    if (text) {  // text will be null if zoomed out beyond label display scale
      featureVals.font = text.getFont();
      featureVals.fontcolor = text.getFill().getColor();
      fontOutline = text.getStroke();
      if (fontOutline) {
        featureVals.fontoutlinecolor = fontOutline.getColor();
        featureVals.fontoutlinewidth = fontOutline.getWidth();
      } else {
        featureVals.fontoutlinecolor = '#ffffff';
        featureVals.fontoutlinewidth = '0';
      }
    }
    stylecontrols.setVals(featureVals);
  }

  return {setControls: setControls};

}
